/*
 * Purpose: Local analytics buffer — mock auth / offline admin fallback
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import type { AnalyticsEventPayload, AnalyticsSummary, PricingSubheadExperimentSummary } from '@/lib/siteAnalyticsTypes';
import { PRICING_SUBHEAD_EXPERIMENT_ID } from '@/lib/pricingSubheadExperiment';

const KEY = 'autoblocks:analytics-local';
const MAX = 500;

interface StoredEvent extends AnalyticsEventPayload {
  timestamp: string;
}

function readEvents(): StoredEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StoredEvent[]) : [];
  } catch {
    return [];
  }
}

function writeEvents(events: StoredEvent[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(KEY, JSON.stringify(events.slice(0, MAX)));
}

export function bufferLocalAnalyticsEvent(event: AnalyticsEventPayload): void {
  const events = readEvents();
  events.unshift({
    ...event,
    timestamp: new Date().toISOString(),
  });
  writeEvents(events);
}

export function buildLocalAnalyticsSummary(days = 14): AnalyticsSummary {
  const events = readEvents();
  const today = new Date();
  const daily = [];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const dayEvents = events.filter((e) => e.timestamp.startsWith(key));
    const users = new Set(dayEvents.map((e) => e.anonymousId).filter(Boolean));
    daily.push({
      date: key,
      pageViews: dayEvents.filter((e) => e.type === 'page_view').length,
      sessions: dayEvents.filter((e) => e.type === 'session_start').length,
      errors: dayEvents.filter((e) => e.type === 'error').length,
      uniqueUsers: users.size,
    });
  }

  const totals = daily.reduce(
    (acc, r) => ({
      pageViews: acc.pageViews + r.pageViews,
      sessions: acc.sessions + r.sessions,
      errors: acc.errors + r.errors,
      uniqueUsers: acc.uniqueUsers + r.uniqueUsers,
    }),
    { pageViews: 0, sessions: 0, errors: 0, uniqueUsers: 0 }
  );

  const pathCounts = new Map<string, number>();
  const userCounts = new Map<string, number>();
  for (const evt of events) {
    if (evt.type === 'page_view' && evt.path) {
      pathCounts.set(evt.path, (pathCounts.get(evt.path) ?? 0) + 1);
    }
    const uid = evt.anonymousId || 'anon';
    userCounts.set(uid, (userCounts.get(uid) ?? 0) + 1);
  }

  return {
    storage: 'local',
    days,
    daily,
    totals,
    topPaths: [...pathCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([path, count]) => ({ path, count })),
    topUsers: [...userCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([userId, count]) => ({ userId, events: count })),
    recentErrors: events.filter((e) => e.type === 'error').slice(0, 50),
    eventCount: events.length,
  };
}

export function buildPricingSubheadExperimentSummary(days = 14): PricingSubheadExperimentSummary {
  const events = readEvents();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffIso = cutoff.toISOString();

  const stats = {
    a: { impressions: 0, pricingTableViews: 0, supportClicks: 0 },
    b: { impressions: 0, pricingTableViews: 0, supportClicks: 0 },
  };

  for (const evt of events) {
    if (evt.type !== 'custom') continue;
    if (evt.meta?.experiment !== PRICING_SUBHEAD_EXPERIMENT_ID) continue;
    if (evt.timestamp < cutoffIso) continue;
    const variant = evt.meta?.variant;
    if (variant !== 'a' && variant !== 'b') continue;
    const action = evt.meta?.action;
    if (action === 'impression') stats[variant].impressions += 1;
    if (action === 'pricing_table_view') stats[variant].pricingTableViews += 1;
    if (action === 'support_click') stats[variant].supportClicks += 1;
  }

  const variants = (['a', 'b'] as const).map((variant) => {
    const row = stats[variant];
    const conversionRate =
      row.impressions > 0 ? Math.round((row.pricingTableViews / row.impressions) * 1000) / 10 : 0;
    return {
      variant,
      label: variant === 'b' ? 'B — Zapier attack + benefit' : 'A — benefit only',
      impressions: row.impressions,
      pricingTableViews: row.pricingTableViews,
      supportClicks: row.supportClicks,
      conversionRate,
    };
  });

  const totalImpressions = variants.reduce((n, v) => n + v.impressions, 0);
  let leader: PricingSubheadExperimentSummary['leader'] = 'insufficient_data';
  let leaderDetail = 'Need more impressions on both variants (min 10 each).';

  if (variants.every((v) => v.impressions >= 10)) {
    const [a, b] = variants;
    const diff = b.conversionRate - a.conversionRate;
    if (Math.abs(diff) < 1) {
      leader = 'tie';
      leaderDetail = `Conversion rates within 1pt (A ${a.conversionRate}% · B ${b.conversionRate}%).`;
    } else if (diff > 0) {
      leader = 'b';
      leaderDetail = `B leads by ${diff.toFixed(1)}pt table-view rate (${b.conversionRate}% vs ${a.conversionRate}%).`;
    } else {
      leader = 'a';
      leaderDetail = `A leads by ${Math.abs(diff).toFixed(1)}pt table-view rate (${a.conversionRate}% vs ${b.conversionRate}%).`;
    }
  } else if (totalImpressions > 0) {
    leaderDetail = `${totalImpressions} impression(s) in local buffer — accept analytics cookies on /pricing to record live events.`;
  }

  return {
    experimentId: PRICING_SUBHEAD_EXPERIMENT_ID,
    days,
    storage: 'local',
    updatedAt: new Date().toISOString(),
    variants,
    leader,
    leaderDetail,
  };
}
